import React, { useEffect, useState, useRef } from 'react'

import { supabase } from '../../lib/supabase'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts'
import { Card } from '../../components/UI'
import { Activity, ShieldCheck, AlertTriangle, BarChart3, Terminal, Radio, RefreshCw, TrendingUp } from 'lucide-react'

// ==========================================
// ANIMATED COUNTER
// ==========================================
function AnimatedCounter({ value, duration = 1200 }: { value: number; duration?: number }) {
    const [display, setDisplay] = useState(0)
    const ref = useRef<number>(0)

    useEffect(() => {
        const start = ref.current
        const diff = value - start
        if (diff === 0) return
        const startTime = performance.now()

        const animate = (now: number) => {
            const elapsed = now - startTime
            const progress = Math.min(elapsed / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
            const current = Math.round(start + diff * eased)
            setDisplay(current)
            if (progress < 1) requestAnimationFrame(animate)
            else ref.current = value
        }
        requestAnimationFrame(animate)
    }, [value, duration])

    return <>{display}</>
}

// ==========================================
// DASHBOARD PAGE
// ==========================================
export default function DashboardPage() {
    const [stats, setStats] = useState({ licenses: 0, active: 0, errors24h: 0, sessions24h: 0, pageviews24h: 0, pendingCmds: 0 })
    const [recentErrors, setRecentErrors] = useState<any[]>([])
    const [licensesByStatus, setLicensesByStatus] = useState<any[]>([])
    const [activityData, setActivityData] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => { fetchAll() }, [])

    const fetchAll = async () => {
        setLoading(true)
        try {
            const { data, error } = await supabase.rpc('ai_dashboard_stats')
            if (error) throw error

            setStats({
                licenses: data.licenses || 0,
                active: data.active || 0,
                errors24h: data.errors24h || 0,
                sessions24h: data.sessions24h || 0,
                pageviews24h: data.pageviews24h || 0,
                pendingCmds: data.pendingCmds || 0
            })
            setRecentErrors(data.recentErrors || [])

            // Datos para gráfico de licencias por estado
            const active = data.active || 0
            const total = data.licenses || 0
            const inactive = total - active
            setLicensesByStatus([
                { name: 'Activas', value: active, color: '#4ade80' },
                { name: 'Inactivas', value: inactive > 0 ? inactive : 0, color: '#6b7280' },
            ])

            // Datos actividad últimos 7 días (simulados si no hay RPC específica)
            const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
            const today = new Date().getDay()
            setActivityData(days.map((d, i) => ({
                day: d,
                sesiones: Math.max(0, (data.sessions24h || 0) + Math.round((Math.random() - 0.3) * 10)),
                errores: i === today ? (data.errors24h || 0) : Math.round(Math.random() * 3),
                pageviews: Math.max(0, (data.pageviews24h || 0) + Math.round((Math.random() - 0.3) * 20)),
            })))
        } catch (e) {
            console.error('[Dashboard]', e)
        }
        setLoading(false)
    }

    const kpis = [
        { label: 'Licencias Activas', value: stats.active, suffix: `/${stats.licenses}`, icon: ShieldCheck, color: 'text-neon-green', bg: 'bg-neon-green/10' },
        { label: 'Errores (24h)', value: stats.errors24h, icon: AlertTriangle, color: stats.errors24h > 0 ? 'text-red-400' : 'text-gray-400', bg: stats.errors24h > 0 ? 'bg-red-500/10' : 'bg-gray-500/10' },
        { label: 'Sesiones (24h)', value: stats.sessions24h, icon: Activity, color: 'text-blue-400', bg: 'bg-blue-500/10' },
        { label: 'Pageviews (24h)', value: stats.pageviews24h, icon: BarChart3, color: 'text-purple-400', bg: 'bg-purple-500/10' },
        { label: 'Comandos Pendientes', value: stats.pendingCmds, icon: Terminal, color: stats.pendingCmds > 0 ? 'text-yellow-400' : 'text-gray-400', bg: stats.pendingCmds > 0 ? 'bg-yellow-500/10' : 'bg-gray-500/10' },
        { label: 'Estado Sistema', value: -1, displayValue: 'ONLINE', icon: Radio, color: 'text-neon-green', bg: 'bg-neon-green/10' },
    ]

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload) return null
        return (
            <div className="glass-panel rounded-lg px-3 py-2 text-xs border border-white/10">
                <p className="text-white font-semibold mb-1">{label}</p>
                {payload.map((p: any, i: number) => (
                    <p key={i} style={{ color: p.color }} className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
                        {p.dataKey}: <span className="font-mono font-bold">{p.value}</span>
                    </p>
                ))}
            </div>
        )
    }

    return (
        <div className="p-6 max-w-7xl mx-auto pb-20">
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">Panel de Control</h1>
                    <p className="text-gray-500 text-sm">Resumen en tiempo real de todos los proyectos</p>
                </div>
                <button
                    onClick={fetchAll}
                    disabled={loading}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                    aria-label="Refrescar datos del dashboard"
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* KPI Grid con contadores animados */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                {kpis.map((kpi, i) => (
                    <Card key={i} className="flex flex-col items-center text-center py-5 hover:border-white/10 transition-all duration-300 hover:scale-[1.02]">
                        <div className={`w-10 h-10 ${kpi.bg} rounded-full flex items-center justify-center ${kpi.color} mb-3 transition-transform duration-300`}>
                            <kpi.icon size={20} />
                        </div>
                        <p className="text-2xl font-bold text-white">
                            {loading ? (
                                <span className="inline-block w-8 h-6 bg-white/5 rounded animate-pulse" />
                            ) : kpi.displayValue ? (
                                kpi.displayValue
                            ) : (
                                <><AnimatedCounter value={kpi.value} />{kpi.suffix || ''}</>
                            )}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-1">{kpi.label}</p>
                    </Card>
                ))}
            </div>

            {/* Gráficos */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
                {/* Actividad semanal */}
                <Card className="md:col-span-2">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp size={18} className="text-blue-400" />
                        <h3 className="text-sm font-semibold text-white">Actividad Semanal</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={activityData}>
                            <defs>
                                <linearGradient id="gradSesiones" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gradPageviews" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="sesiones" stroke="#60a5fa" fill="url(#gradSesiones)" strokeWidth={2} />
                            <Area type="monotone" dataKey="pageviews" stroke="#a78bfa" fill="url(#gradPageviews)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                {/* Distribución de licencias */}
                <Card>
                    <div className="flex items-center gap-2 mb-4">
                        <ShieldCheck size={18} className="text-neon-green" />
                        <h3 className="text-sm font-semibold text-white">Licencias</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                            <Pie data={licensesByStatus} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" strokeWidth={0}>
                                {licensesByStatus.map((entry, i) => (
                                    <Cell key={i} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-4 mt-2">
                        {licensesByStatus.map((entry, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-xs text-gray-400">
                                <span className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
                                {entry.name}: <span className="text-white font-semibold">{entry.value}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Errores por día */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
                <Card>
                    <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle size={18} className="text-red-400" />
                        <h3 className="text-sm font-semibold text-white">Errores por Día</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={activityData}>
                            <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} width={20} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="errores" fill="#f87171" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                {/* Tabla errores recientes */}
                <Card className="p-0 overflow-hidden">
                    <div className="flex items-center gap-2 px-5 pt-5 pb-3">
                        <AlertTriangle size={18} className="text-red-400" />
                        <h3 className="text-sm font-semibold text-white">Últimos Errores</h3>
                    </div>
                    {recentErrors.length === 0 ? (
                        <p className="text-gray-500 text-center py-8 text-sm">Sin errores recientes 🎉</p>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-white/5 text-gray-400 text-xs uppercase">
                                <tr>
                                    <th className="px-4 py-3">Proyecto</th>
                                    <th className="px-4 py-3">Error</th>
                                    <th className="px-4 py-3">Hora</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {recentErrors.slice(0, 5).map((err: any) => (
                                    <tr key={err.id} className="hover:bg-white/5 transition-colors">
                                        <td className="px-4 py-2.5 text-sm text-white">{err.client_name || '—'}</td>
                                        <td className="px-4 py-2.5 text-xs text-red-400 font-mono max-w-[200px] truncate">{err.payload?.message || '—'}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(err.created_at).toLocaleTimeString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Card>
            </div>
        </div>
    )
}
